import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  Collapse,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  ConfirmationNumber as ServiceNowIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Public as FeedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { usePermission } from '../../hooks/usePermission';
import { apiService } from '../../services/apiService';
import { formatRelativeTime } from '../../utils/dateUtils';
import OutboundDestinations from './OutboundDestinations';
import ThreatFeeds from './ThreatFeeds';

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

const emptyJiraForm = {
  enabled: false,
  baseUrl: '',
  email: '',
  apiToken: '',
  clearToken: false,
  projectKey: '',
  issueType: '',
  pushComments: false,
  pullComments: false,
  syncStatus: false,
  closeCaseWhenDone: false,
};

const jiraFormFromServer = (j = {}) => ({
  enabled: Boolean(j.enabled),
  baseUrl: j.baseUrl || '',
  email: j.email || '',
  apiToken: '',
  clearToken: false,
  projectKey: j.projectKey || '',
  issueType: j.issueType || '',
  pushComments: Boolean(j.pushComments),
  pullComments: Boolean(j.pullComments),
  syncStatus: Boolean(j.syncStatus),
  closeCaseWhenDone: Boolean(j.closeCaseWhenDone),
});

const emptySnowForm = {
  enabled: false,
  baseUrl: '',
  username: '',
  clientSecret: '',
  clearPassword: false,
  apiToken: '',
  clearToken: false,
  assignmentGroup: '',
  category: 'Security',
  callerId: '',
  pushComments: true,
  pullComments: true,
  syncStatus: true,
  closeCaseWhenDone: false,
};

const snowFormFromServer = (s = {}) => ({
  enabled: Boolean(s.enabled),
  baseUrl: s.baseUrl || '',
  username: s.username || '',
  clientSecret: '',
  clearPassword: false,
  apiToken: '',
  clearToken: false,
  assignmentGroup: s.assignmentGroup || '',
  category: s.category || 'Security',
  callerId: s.callerId || '',
  pushComments: s.pushComments !== false,
  pullComments: s.pullComments !== false,
  syncStatus: s.syncStatus !== false,
  closeCaseWhenDone: Boolean(s.closeCaseWhenDone),
});

const SystemIntegrations = ({ onDirtyChange }) => {
  const { loading: settingsLoading } = useSettings();
  const { canManageCompany } = usePermission();
  const readOnly = !canManageCompany;

  const [tab, setTab] = useState(0);

  // ---------- Jira ----------
  const [jira, setJira] = useState(null); // last-loaded server state (includes hasApiToken, lastTest)
  const [jiraForm, setJiraForm] = useState(emptyJiraForm);
  const [jiraLoading, setJiraLoading] = useState(true);
  const [jiraSaving, setJiraSaving] = useState(false);
  const [jiraTesting, setJiraTesting] = useState(false);
  const [jiraTestResult, setJiraTestResult] = useState(null);
  const [jiraError, setJiraError] = useState(null);
  const [jiraSuccess, setJiraSuccess] = useState(null);
  const [jiraProjectOptions, setJiraProjectOptions] = useState(null);
  const [jiraIssueTypeOptions, setJiraIssueTypeOptions] = useState(null);
  const [jiraLoadingProjects, setJiraLoadingProjects] = useState(false);

  const loadJira = useCallback(async () => {
    try {
      setJiraLoading(true);
      setJiraError(null);
      const res = await apiService.integrations.getJira();
      const j = res.data?.jira || {};
      setJira(j);
      setJiraForm(jiraFormFromServer(j));
    } catch (err) {
      setJiraError(serverMessage(err, 'Failed to load Jira settings'));
    } finally {
      setJiraLoading(false);
    }
  }, []);

  useEffect(() => { loadJira(); }, [loadJira]);

  const jiraReadOnly = readOnly;

  const jiraDirty = useMemo(() => {
    if (!jira) return false;
    return (
      jiraForm.enabled !== Boolean(jira.enabled) ||
      jiraForm.baseUrl.trim() !== (jira.baseUrl || '') ||
      jiraForm.email.trim() !== (jira.email || '') ||
      jiraForm.projectKey.trim() !== (jira.projectKey || '') ||
      jiraForm.issueType.trim() !== (jira.issueType || '') ||
      jiraForm.pushComments !== Boolean(jira.pushComments) ||
      jiraForm.pullComments !== Boolean(jira.pullComments) ||
      jiraForm.syncStatus !== Boolean(jira.syncStatus) ||
      jiraForm.closeCaseWhenDone !== Boolean(jira.closeCaseWhenDone) ||
      Boolean(jiraForm.apiToken.trim()) ||
      jiraForm.clearToken
    );
  }, [jiraForm, jira]);

  const setJiraField = (field, value) => setJiraForm((prev) => ({ ...prev, [field]: value }));

  const handleJiraClearToken = () => setJiraForm((prev) => ({ ...prev, apiToken: '', clearToken: true }));
  const handleJiraUndoClearToken = () => setJiraForm((prev) => ({ ...prev, clearToken: false }));

  const handleJiraSave = async () => {
    if (!jira) return;
    const changes = { enabled: jiraForm.enabled };
    if (jiraForm.baseUrl.trim() !== (jira.baseUrl || '')) changes.baseUrl = jiraForm.baseUrl.trim();
    if (jiraForm.email.trim() !== (jira.email || '')) changes.email = jiraForm.email.trim();
    if (jiraForm.projectKey.trim() !== (jira.projectKey || '')) changes.projectKey = jiraForm.projectKey.trim();
    if (jiraForm.issueType.trim() !== (jira.issueType || '')) changes.issueType = jiraForm.issueType.trim();
    if (jiraForm.pushComments !== Boolean(jira.pushComments)) changes.pushComments = jiraForm.pushComments;
    if (jiraForm.pullComments !== Boolean(jira.pullComments)) changes.pullComments = jiraForm.pullComments;
    if (jiraForm.syncStatus !== Boolean(jira.syncStatus)) changes.syncStatus = jiraForm.syncStatus;
    if (jiraForm.closeCaseWhenDone !== Boolean(jira.closeCaseWhenDone)) changes.closeCaseWhenDone = jiraForm.closeCaseWhenDone;
    if (jiraForm.clearToken) changes.apiToken = null;
    else if (jiraForm.apiToken.trim()) changes.apiToken = jiraForm.apiToken.trim();

    try {
      setJiraSaving(true);
      setJiraError(null);
      const res = await apiService.integrations.updateJira(changes);
      const j = res.data?.jira || {};
      setJira(j);
      setJiraForm(jiraFormFromServer(j));
      setJiraSuccess('Jira settings saved');
      setTimeout(() => setJiraSuccess(null), 3000);
    } catch (err) {
      setJiraError(serverMessage(err, 'Failed to save Jira settings'));
    } finally {
      setJiraSaving(false);
    }
  };

  const handleJiraTest = async () => {
    try {
      setJiraTesting(true);
      setJiraError(null);
      setJiraTestResult(null);
      const res = await apiService.integrations.testJira(jiraForm.projectKey.trim() || undefined);
      setJiraTestResult({ success: true, ...res.data });
    } catch (err) {
      const data = err?.response?.data;
      setJiraTestResult({ success: false, error: data?.error || serverMessage(err, 'Connection test failed') });
    } finally {
      setJiraTesting(false);
      loadJira();
    }
  };

  const handleJiraLoadProjects = async () => {
    try {
      setJiraLoadingProjects(true);
      setJiraError(null);
      const res = await apiService.integrations.jiraProjects(jiraForm.projectKey.trim() || undefined);
      setJiraProjectOptions(res.data?.projects || []);
      setJiraIssueTypeOptions(res.data?.issueTypes || []);
    } catch (err) {
      setJiraError(serverMessage(err, 'Failed to load Jira projects. Check the connection settings and try again.'));
    } finally {
      setJiraLoadingProjects(false);
    }
  };

  const jiraProjectItems = useMemo(() => {
    if (!jiraProjectOptions) return null;
    if (jiraForm.projectKey && !jiraProjectOptions.some((p) => p.key === jiraForm.projectKey)) {
      return [{ key: jiraForm.projectKey, name: jiraForm.projectKey }, ...jiraProjectOptions];
    }
    return jiraProjectOptions;
  }, [jiraProjectOptions, jiraForm.projectKey]);

  const jiraIssueTypeItems = useMemo(() => {
    if (!jiraIssueTypeOptions) return null;
    if (jiraForm.issueType && !jiraIssueTypeOptions.some((t) => t.name === jiraForm.issueType)) {
      return [{ id: jiraForm.issueType, name: jiraForm.issueType }, ...jiraIssueTypeOptions];
    }
    return jiraIssueTypeOptions;
  }, [jiraIssueTypeOptions, jiraForm.issueType]);

  // ---------- ServiceNow ----------
  const [snow, setSnow] = useState(null);
  const [snowForm, setSnowForm] = useState(emptySnowForm);
  const [snowLoading, setSnowLoading] = useState(true);
  const [snowSaving, setSnowSaving] = useState(false);
  const [snowTesting, setSnowTesting] = useState(false);
  const [snowTestResult, setSnowTestResult] = useState(null);
  const [snowError, setSnowError] = useState(null);
  const [snowSuccess, setSnowSuccess] = useState(null);
  const [snowAdvanced, setSnowAdvanced] = useState(false);

  const loadSnow = useCallback(async () => {
    try {
      setSnowLoading(true);
      setSnowError(null);
      const res = await apiService.integrations.getServiceNow();
      const s = res.data?.servicenow || {};
      setSnow(s);
      setSnowForm(snowFormFromServer(s));
    } catch (err) {
      setSnowError(serverMessage(err, 'Failed to load ServiceNow settings'));
    } finally {
      setSnowLoading(false);
    }
  }, []);

  useEffect(() => { loadSnow(); }, [loadSnow]);

  const snowDirty = useMemo(() => {
    if (!snow) return false;
    return (
      snowForm.enabled !== Boolean(snow.enabled) ||
      snowForm.baseUrl.trim() !== (snow.baseUrl || '') ||
      snowForm.username.trim() !== (snow.username || '') ||
      snowForm.assignmentGroup.trim() !== (snow.assignmentGroup || '') ||
      snowForm.category.trim() !== (snow.category || 'Security') ||
      snowForm.callerId.trim() !== (snow.callerId || '') ||
      snowForm.pushComments !== (snow.pushComments !== false) ||
      snowForm.pullComments !== (snow.pullComments !== false) ||
      snowForm.syncStatus !== (snow.syncStatus !== false) ||
      snowForm.closeCaseWhenDone !== Boolean(snow.closeCaseWhenDone) ||
      Boolean(snowForm.clientSecret.trim()) ||
      Boolean(snowForm.apiToken.trim()) ||
      snowForm.clearPassword ||
      snowForm.clearToken
    );
  }, [snowForm, snow]);

  const setSnowField = (field, value) => setSnowForm((prev) => ({ ...prev, [field]: value }));

  const handleSnowSave = async () => {
    if (!snow) return;
    const changes = { enabled: snowForm.enabled };
    if (snowForm.baseUrl.trim() !== (snow.baseUrl || '')) changes.baseUrl = snowForm.baseUrl.trim();
    if (snowForm.username.trim() !== (snow.username || '')) changes.username = snowForm.username.trim();
    if (snowForm.assignmentGroup.trim() !== (snow.assignmentGroup || '')) changes.assignmentGroup = snowForm.assignmentGroup.trim();
    if (snowForm.category.trim() !== (snow.category || 'Security')) changes.category = snowForm.category.trim();
    if (snowForm.callerId.trim() !== (snow.callerId || '')) changes.callerId = snowForm.callerId.trim();
    if (snowForm.pushComments !== (snow.pushComments !== false)) changes.pushComments = snowForm.pushComments;
    if (snowForm.pullComments !== (snow.pullComments !== false)) changes.pullComments = snowForm.pullComments;
    if (snowForm.syncStatus !== (snow.syncStatus !== false)) changes.syncStatus = snowForm.syncStatus;
    if (snowForm.closeCaseWhenDone !== Boolean(snow.closeCaseWhenDone)) changes.closeCaseWhenDone = snowForm.closeCaseWhenDone;
    if (snowForm.clearPassword) changes.clientSecret = null;
    else if (snowForm.clientSecret.trim()) changes.clientSecret = snowForm.clientSecret.trim();
    if (snowForm.clearToken) changes.apiToken = null;
    else if (snowForm.apiToken.trim()) changes.apiToken = snowForm.apiToken.trim();

    try {
      setSnowSaving(true);
      setSnowError(null);
      const res = await apiService.integrations.updateServiceNow(changes);
      const s = res.data?.servicenow || {};
      setSnow(s);
      setSnowForm(snowFormFromServer(s));
      setSnowSuccess('ServiceNow settings saved');
      setTimeout(() => setSnowSuccess(null), 3000);
    } catch (err) {
      setSnowError(serverMessage(err, 'Failed to save ServiceNow settings'));
    } finally {
      setSnowSaving(false);
    }
  };

  const handleSnowTest = async () => {
    try {
      setSnowTesting(true);
      setSnowError(null);
      setSnowTestResult(null);
      const res = await apiService.integrations.testServiceNow();
      setSnowTestResult({ success: true, ...res.data });
    } catch (err) {
      const data = err?.response?.data;
      setSnowTestResult({ success: false, error: data?.error || serverMessage(err, 'Connection test failed') });
    } finally {
      setSnowTesting(false);
      loadSnow();
    }
  };

  // ---------- Dirty-state guard (tab switch + page unload) ----------
  const dirty = jiraDirty || snowDirty;

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const lastTestLine = (lastTest) => (
    lastTest && (lastTest.at || lastTest.success !== undefined) ? (
      <Chip
        size="small"
        color={lastTest.success ? 'success' : 'error'}
        variant="outlined"
        label={`Last test ${lastTest.success ? 'succeeded' : 'failed'}${lastTest.at ? ` ${formatRelativeTime(lastTest.at)}` : ''}`}
      />
    ) : null
  );

  // ---------- Jira card ----------
  const jiraCard = (
    <Card sx={{ mb: 3 }}>
      <CardHeader avatar={<BugReportIcon />} title="Jira" subheader="Sync cases with a Jira Cloud project" />
      <CardContent>
        {jiraLoading ? (
          <Box display="flex" justifyContent="center" py={2}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {jiraError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJiraError(null)}>{jiraError}</Alert>}
            {jiraSuccess && <Alert severity="success" sx={{ mb: 2 }}>{jiraSuccess}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={jiraForm.enabled}
                      onChange={(e) => setJiraField('enabled', e.target.checked)}
                      disabled={jiraReadOnly}
                    />
                  }
                  label="Enabled"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Base URL"
                  value={jiraForm.baseUrl}
                  onChange={(e) => setJiraField('baseUrl', e.target.value)}
                  placeholder="https://acme.atlassian.net"
                  disabled={jiraReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={jiraForm.email}
                  onChange={(e) => setJiraField('email', e.target.value)}
                  placeholder="you@acme.com"
                  disabled={jiraReadOnly}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  type="password"
                  label="API token"
                  value={jiraForm.apiToken}
                  onChange={(e) => setJiraField('apiToken', e.target.value)}
                  placeholder={jira?.hasApiToken ? 'configured' : ''}
                  helperText={jiraForm.clearToken ? 'Token will be cleared when you save.' : 'Leave blank to keep the existing token.'}
                  disabled={jiraReadOnly || jiraForm.clearToken}
                />
              </Grid>
              <Grid item xs={12} sm={4} display="flex" alignItems="center">
                {jiraForm.clearToken ? (
                  <Button size="small" onClick={handleJiraUndoClearToken} disabled={jiraReadOnly}>
                    Undo clear
                  </Button>
                ) : (
                  <Button size="small" color="error" onClick={handleJiraClearToken} disabled={jiraReadOnly || !jira?.hasApiToken}>
                    Clear
                  </Button>
                )}
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} sm={5}>
                {jiraProjectItems ? (
                  <FormControl fullWidth disabled={jiraReadOnly}>
                    <InputLabel>Project key</InputLabel>
                    <Select value={jiraForm.projectKey} label="Project key" onChange={(e) => setJiraField('projectKey', e.target.value)}>
                      {jiraProjectItems.map((p) => (
                        <MenuItem key={p.key} value={p.key}>{p.name ? `${p.key} — ${p.name}` : p.key}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Project key"
                    value={jiraForm.projectKey}
                    onChange={(e) => setJiraField('projectKey', e.target.value)}
                    placeholder="SEC"
                    disabled={jiraReadOnly}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {jiraIssueTypeItems ? (
                  <FormControl fullWidth disabled={jiraReadOnly}>
                    <InputLabel>Issue type</InputLabel>
                    <Select value={jiraForm.issueType} label="Issue type" onChange={(e) => setJiraField('issueType', e.target.value)}>
                      {jiraIssueTypeItems.map((t) => (
                        <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Issue type"
                    value={jiraForm.issueType}
                    onChange={(e) => setJiraField('issueType', e.target.value)}
                    placeholder="Task"
                    disabled={jiraReadOnly}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={3} display="flex" alignItems="center">
                <Button
                  size="small"
                  onClick={handleJiraLoadProjects}
                  disabled={jiraReadOnly || jiraLoadingProjects}
                  startIcon={jiraLoadingProjects ? <CircularProgress size={14} /> : null}
                >
                  Load projects
                </Button>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={jiraForm.pushComments} onChange={(e) => setJiraField('pushComments', e.target.checked)} disabled={jiraReadOnly} />}
                  label="Push case comments to Jira"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={jiraForm.pullComments} onChange={(e) => setJiraField('pullComments', e.target.checked)} disabled={jiraReadOnly} />}
                  label="Pull Jira comments into the case"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={jiraForm.syncStatus} onChange={(e) => setJiraField('syncStatus', e.target.checked)} disabled={jiraReadOnly} />}
                  label="Sync issue status"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={jiraForm.closeCaseWhenDone} onChange={(e) => setJiraField('closeCaseWhenDone', e.target.checked)} disabled={jiraReadOnly} />}
                  label="Resolve case when the Jira issue is done"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Status and comment sync with Jira runs automatically every 30 minutes.
                </Typography>
                {lastTestLine(jira?.lastTest)}
                {jira?.lastTest && !jira.lastTest.success && jira.lastTest.error && (
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                    {jira.lastTest.error}
                  </Typography>
                )}
              </Grid>

              {jiraTestResult && (
                <Grid item xs={12}>
                  <Alert severity={jiraTestResult.success ? 'success' : 'error'} onClose={() => setJiraTestResult(null)}>
                    {jiraTestResult.success
                      ? `Connected as ${jiraTestResult.user || 'unknown user'}${jiraTestResult.project ? ` · Project: ${jiraTestResult.project}` : ''}${jiraTestResult.responseTime ? ` (${jiraTestResult.responseTime} ms)` : ''}`
                      : (jiraTestResult.error || 'Connection test failed')}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  onClick={handleJiraTest}
                  disabled={jiraTesting || jiraSaving}
                  startIcon={jiraTesting ? <CircularProgress size={16} /> : null}
                >
                  Test connection
                </Button>
                <Tooltip title={jiraReadOnly ? 'Requires company management permission' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={jiraSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleJiraSave}
                      disabled={jiraSaving || jiraReadOnly || !jiraDirty}
                    >
                      {jiraSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );

  // ---------- ServiceNow card ----------
  const serviceNowCard = (
    <Card sx={{ mb: 3 }}>
      <CardHeader avatar={<ServiceNowIcon />} title="ServiceNow" subheader="Raise and track incidents on a ServiceNow instance" />
      <CardContent>
        {snowLoading ? (
          <Box display="flex" justifyContent="center" py={2}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {snowError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSnowError(null)}>{snowError}</Alert>}
            {snowSuccess && <Alert severity="success" sx={{ mb: 2 }}>{snowSuccess}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={snowForm.enabled}
                      onChange={(e) => setSnowField('enabled', e.target.checked)}
                      disabled={readOnly}
                    />
                  }
                  label="Enabled"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instance URL"
                  value={snowForm.baseUrl}
                  onChange={(e) => setSnowField('baseUrl', e.target.value)}
                  placeholder="https://acme.service-now.com"
                  helperText="Must be https."
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={snowForm.username}
                  onChange={(e) => setSnowField('username', e.target.value)}
                  placeholder="excubia.integration"
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  value={snowForm.clientSecret}
                  onChange={(e) => setSnowField('clientSecret', e.target.value)}
                  placeholder={snow?.hasPassword ? 'configured' : ''}
                  helperText={snowForm.clearPassword ? 'Password will be cleared when you save.' : 'Leave blank to keep the existing password.'}
                  disabled={readOnly || snowForm.clearPassword}
                />
              </Grid>
              <Grid item xs={12} sm={4} display="flex" alignItems="center">
                {snowForm.clearPassword ? (
                  <Button size="small" onClick={() => setSnowForm((prev) => ({ ...prev, clearPassword: false }))} disabled={readOnly}>
                    Undo clear
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setSnowForm((prev) => ({ ...prev, clientSecret: '', clearPassword: true }))}
                    disabled={readOnly || !snow?.hasPassword}
                  >
                    Clear
                  </Button>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  size="small"
                  onClick={() => setSnowAdvanced((v) => !v)}
                  endIcon={snowAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  Advanced authentication
                </Button>
                <Collapse in={snowAdvanced}>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        type="password"
                        label="Bearer token (optional)"
                        value={snowForm.apiToken}
                        onChange={(e) => setSnowField('apiToken', e.target.value)}
                        placeholder={snow?.hasToken ? 'configured' : ''}
                        helperText={
                          snowForm.clearToken
                            ? 'Token will be cleared when you save.'
                            : 'An OAuth bearer token used instead of basic auth. Leave blank to keep the existing token.'
                        }
                        disabled={readOnly || snowForm.clearToken}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} display="flex" alignItems="center">
                      {snowForm.clearToken ? (
                        <Button size="small" onClick={() => setSnowForm((prev) => ({ ...prev, clearToken: false }))} disabled={readOnly}>
                          Undo clear
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setSnowForm((prev) => ({ ...prev, apiToken: '', clearToken: true }))}
                          disabled={readOnly || !snow?.hasToken}
                        >
                          Clear
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Assignment group"
                  value={snowForm.assignmentGroup}
                  onChange={(e) => setSnowField('assignmentGroup', e.target.value)}
                  placeholder="Security Operations"
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Category"
                  value={snowForm.category}
                  onChange={(e) => setSnowField('category', e.target.value)}
                  placeholder="Security"
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Caller"
                  value={snowForm.callerId}
                  onChange={(e) => setSnowField('callerId', e.target.value)}
                  placeholder="sys_id or user name"
                  helperText="Optional."
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={snowForm.pushComments} onChange={(e) => setSnowField('pushComments', e.target.checked)} disabled={readOnly} />}
                  label="Push case comments as work notes"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={snowForm.pullComments} onChange={(e) => setSnowField('pullComments', e.target.checked)} disabled={readOnly} />}
                  label="Pull work notes and comments into the case"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={snowForm.syncStatus} onChange={(e) => setSnowField('syncStatus', e.target.checked)} disabled={readOnly} />}
                  label="Sync incident state"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={snowForm.closeCaseWhenDone} onChange={(e) => setSnowField('closeCaseWhenDone', e.target.checked)} disabled={readOnly} />}
                  label="Resolve case when the incident is resolved or closed"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  State and comment sync runs on the same 30-minute schedule as Jira. Create and unlink incidents from a case page.
                </Typography>
                {lastTestLine(snow?.lastTest)}
                {snow?.lastTest && !snow.lastTest.success && snow.lastTest.error && (
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                    {snow.lastTest.error}
                  </Typography>
                )}
              </Grid>

              {snowTestResult && (
                <Grid item xs={12}>
                  <Alert severity={snowTestResult.success ? 'success' : 'error'} onClose={() => setSnowTestResult(null)}>
                    {snowTestResult.success
                      ? `Connected as ${snowTestResult.user || 'unknown user'}${snowTestResult.assignmentGroup ? ` · Assignment group: ${snowTestResult.assignmentGroup}` : ''}${snowTestResult.responseTime ? ` (${snowTestResult.responseTime} ms)` : ''}`
                      : (snowTestResult.error || 'Connection test failed')}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  onClick={handleSnowTest}
                  disabled={snowTesting || snowSaving || readOnly}
                  startIcon={snowTesting ? <CircularProgress size={16} /> : null}
                >
                  Test connection
                </Button>
                <Tooltip title={readOnly ? 'Requires company management permission' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={snowSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSnowSave}
                      disabled={snowSaving || readOnly || !snowDirty}
                    >
                      {snowSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );

  if (settingsLoading && jiraLoading && snowLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view the integration configuration but changing it requires the company management permission.
        </Alert>
      )}
      {dirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved ticketing changes. Save them before leaving this tab or they will be lost.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<BugReportIcon />} iconPosition="start" label="Ticketing" />
        <Tab icon={<SendIcon />} iconPosition="start" label="Outbound destinations" />
        <Tab icon={<FeedIcon />} iconPosition="start" label="Threat intelligence" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {jiraCard}
          {serviceNowCard}
        </Box>
      )}
      {tab === 1 && <OutboundDestinations readOnly={readOnly} />}
      {tab === 2 && <ThreatFeeds readOnly={readOnly} />}
    </Box>
  );
};

export default SystemIntegrations;
